/**
 * Phase F-finale-6 Slice 6b — IA self-scoped admission-stages API client.
 *
 * Wraps the tenant-self BE surface added in Slice 6a (eduviaos-backend
 * PR #153):
 *
 *   GET    /admission-stages
 *   PUT    /admission-stages
 *   POST   /admission-stages/reset
 *   PATCH  /admission-stages/{stage_key}/form
 *   GET    /admission-stages/funnel
 *
 * Tenant is resolved BE-side from the `X-Tenant-Slug` header + JWT, so
 * these hit the bare `/admission-stages` surface (NOT the SA
 * `/super-admin/tenants/{slug}/...` routes). Reads need
 * `VIEW_ADMISSIONS`; writes need `MANAGE_ADMISSIONS` +
 * `admissions_editable_by_tenant` (enforced server-side via
 * `require_admissions_config_editing`; the FE additionally gates the UI
 * via `useCanEditAdmissions`).
 *
 * The wire shapes mirror `app/domains/onboarding/schemas.py` exactly —
 * any field-shape change on the BE must land here too. They intentionally
 * duplicate the SA-portal copy in `apps/super-admin/src/lib/admissionStages/api.ts`
 * rather than sharing a package, because the two portals scope their
 * routes + query keys differently; promotion to `@eduviaos/shared` is a
 * later refactor if a third consumer appears.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type TerminalOutcome = "admitted" | "rejected" | null;

export interface LinkedFormSummary {
  slug: string;
  name: string;
  version: number;
  field_count: number;
}

/** One action-gated requirement on a stage (Phase F0/F3). Mirrors
 *  `app.domains.onboarding.schemas.StageRequirementItem` 1:1. */
export type StageRequirementKind =
  | "form_submission"
  | "document_upload"
  | "assessment_attempt"
  | "interview_completed"
  | "manual_check";

export interface StageRequirementWire {
  key: string;
  kind: StageRequirementKind;
  label: string;
  config: Record<string, unknown>;
}

export interface AdmissionStageItem {
  key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint: string | null;
  auto_advance_days: number | null;
  required_documents: string[];
  linked_form: LinkedFormSummary | null;
  requirements: StageRequirementWire[];
}

export type AdmissionStageSource = "tenant" | "vertical_default";

export interface AdmissionStagesResponse {
  tenant_slug: string;
  vertical: string;
  source: AdmissionStageSource;
  allow_direct_admission: boolean;
  allow_skip_stages: boolean;
  allow_revert: boolean;
  duplicate_detection: boolean;
  stages: AdmissionStageItem[];

  /** CAS token for the tenant's admission-draft override doc. Int
   *  seconds-since-epoch derived from `AdmissionStageConfig.updated_at`;
   *  `0` when the tenant has no override yet (`source ===
   *  "vertical_default"`). The capability-AI assist flow (Slice 6c)
   *  echoes this on suggest + apply so the BE can 409 a stale
   *  suggestion. */
  draft_version: number;
}

/** Wire shape sent on PUT — mirrors `WriteAdmissionStagesRequest`. */
export interface WriteAdmissionStageBody {
  key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint?: string | null;
  auto_advance_days?: number | null;
  required_documents?: string[];
  requirements?: StageRequirementWire[];
}

export interface WriteAdmissionStagesRequest {
  stages: WriteAdmissionStageBody[];
  allow_direct_admission: boolean;
  allow_skip_stages: boolean;
  allow_revert: boolean;
  duplicate_detection: boolean;
}

export interface FunnelStageItem {
  stage_key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: TerminalOutcome;
  color_hint: string | null;
  count: number;
}

export interface AdmissionFunnelResponse {
  tenant_slug: string;
  vertical: string;
  stages: FunnelStageItem[];
  total: number;
}

export interface LinkStageFormRequest {
  /** `null` to unlink the stage from any form. */
  form_slug: string | null;
}

// ── React Query hooks ─────────────────────────────────────────────────

const stagesKey = ["ia", "admission-stages"] as const;
const funnelKey = ["ia", "admission-stages", "funnel"] as const;

/** The cache-key prefix both reads share — invalidate this to refetch
 *  the stages config + funnel report together (used by Slice 6c after an
 *  AI apply mutates the server draft). */
export const admissionStagesKeyPrefix = stagesKey;

export function useAdmissionStages() {
  return useQuery<AdmissionStagesResponse>({
    queryKey: stagesKey,
    queryFn: () => api.get<AdmissionStagesResponse>("/admission-stages"),
    staleTime: 15_000,
  });
}

export function useAdmissionFunnel() {
  return useQuery<AdmissionFunnelResponse>({
    queryKey: funnelKey,
    queryFn: () => api.get<AdmissionFunnelResponse>("/admission-stages/funnel"),
    staleTime: 30_000,
  });
}

export function useWriteAdmissionStages() {
  const qc = useQueryClient();
  return useMutation<AdmissionStagesResponse, ApiError, WriteAdmissionStagesRequest>({
    mutationFn: (body) => api.put<AdmissionStagesResponse>("/admission-stages", body),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey, data);
      void qc.invalidateQueries({ queryKey: funnelKey });
    },
  });
}

export function useResetAdmissionStages() {
  const qc = useQueryClient();
  return useMutation<AdmissionStagesResponse, ApiError, void>({
    mutationFn: () => api.post<AdmissionStagesResponse>("/admission-stages/reset", undefined),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey, data);
      void qc.invalidateQueries({ queryKey: funnelKey });
    },
  });
}

export function useLinkStageForm() {
  const qc = useQueryClient();
  return useMutation<
    AdmissionStagesResponse,
    ApiError,
    { stageKey: string; body: LinkStageFormRequest }
  >({
    mutationFn: ({ stageKey, body }) =>
      api.patch<AdmissionStagesResponse>(
        `/admission-stages/${encodeURIComponent(stageKey)}/form`,
        body,
      ),
    onSuccess: (data) => {
      qc.setQueryData(stagesKey, data);
      void qc.invalidateQueries({ queryKey: funnelKey });
    },
  });
}

// ── Helpers (mirrors apps/super-admin/src/lib/admissionStages/api.ts) ──

/** Default colour palette indexed by stage order. Stages with
 *  `color_hint` set override this. */
export const STAGE_FALLBACK_COLORS = [
  "var(--color-chart-1, oklch(0.62 0.15 155))",
  "var(--color-chart-2, oklch(0.6 0.16 270))",
  "var(--color-chart-3, oklch(0.7 0.17 50))",
  "var(--color-chart-4, oklch(0.65 0.13 320))",
  "var(--color-chart-5, oklch(0.6 0.05 250))",
  "var(--color-chart-1, oklch(0.62 0.15 155))",
  "var(--color-chart-2, oklch(0.6 0.16 270))",
  "var(--color-chart-3, oklch(0.7 0.17 50))",
  "var(--color-chart-4, oklch(0.65 0.13 320))",
  "var(--color-chart-5, oklch(0.6 0.05 250))",
] as const;

export function resolveStageColor(stage: { color_hint: string | null; order: number }): string {
  if (stage.color_hint && stage.color_hint.trim() !== "") return stage.color_hint;
  const idx =
    ((stage.order % STAGE_FALLBACK_COLORS.length) + STAGE_FALLBACK_COLORS.length) %
    STAGE_FALLBACK_COLORS.length;
  return STAGE_FALLBACK_COLORS[idx] ?? STAGE_FALLBACK_COLORS[0]!;
}

/** DNS-safe slug derivation for a net-new stage key. Existing keys are
 *  never mutated client-side (the BE locks keys with live enquiries). */
export function deriveStageKey(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "stage"
  );
}

/** DNS-safe slug derivation for a requirement key (Phase F3). Capped at
 *  60 chars to match `Field(min_length=2, max_length=60)`. */
export function deriveRequirementKey(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60) || "requirement"
  );
}

export const REQUIREMENT_KIND_LABELS: Record<StageRequirementKind, string> = {
  form_submission: "Form submission",
  document_upload: "Document upload",
  assessment_attempt: "Assessment attempt",
  interview_completed: "Interview completed",
  manual_check: "Manual check",
};

export const REQUIREMENT_KIND_HELP: Record<StageRequirementKind, string> = {
  form_submission:
    "Linked form on this stage must have a saved payload on the enquiry. Evaluator ships in Phase F0.",
  manual_check:
    "Admin-side toggle on the detail page (e.g. 'ID document verified'). Evaluator ships in Phase F0.",
  document_upload:
    "A named document slot must have a non-null upload reference on the enquiry. Evaluator ships in Phase F4.",
  assessment_attempt:
    "Linked assessment template must have a graded attempt. Evaluator ships in Phase F8.",
  interview_completed:
    "Linked interview must be conducted with a non-empty outcome. Evaluator ships in Phase F13.",
};

export const REQUIREMENT_KIND_LIVE_EVALUATOR: Record<StageRequirementKind, boolean> = {
  form_submission: true,
  manual_check: true,
  document_upload: false,
  assessment_attempt: false,
  interview_completed: false,
};
